import { reaction } from 'mobx';
import { v4 } from 'uuid';
import { useWorkspaceStore } from '../panels/use-workspace-store';
import { useActionBroadcaster } from './use-action-broadcaster';
import { CadNextAction } from './types';
import { CadNextActionHandlerContextValue } from './action-broadcaster-context';
import { AckSource } from '@m43/action-broadcaster';
import { useEffect, useRef } from 'react';

export type ActionHandler<T extends CadNextAction['targetWindowType']> = (
    action: Extract<CadNextAction, { targetWindowType: T }>
) => boolean | undefined;

type IntegratedActionBroadcasterOptions<T extends CadNextAction['targetWindowType']> = {
    id?: string;
    windowType: T;
    /**
     * This flag enables windows to subscribe to actions for channels which do not map to their current window type.
     * This is mainly useful when listening to `ROOT` actions
     */
    ignoreActiveWorkspaceWindowType?: boolean;
    /**
     * The handler is optional so that we can forgo passing it in if we
     * wish to defer handling events until a later time
     */
    handler?: ActionHandler<T>;
};

export function useIntegratedActionBroadcaster<T extends CadNextAction['targetWindowType']>(
    options?: IntegratedActionBroadcasterOptions<T>
): Pick<CadNextActionHandlerContextValue, 'trigger'> {
    const actionBroadcaster = useActionBroadcaster();
    const workspacePanelsStore = useWorkspaceStore();
    const actionBroadcasterDisposerRef = useRef<undefined | (() => void)>(undefined);
    const { handler, windowType, id, ignoreActiveWorkspaceWindowType } = options ?? {};

    // local variable to ensure that when `ignoreActiveWorkspaceWindowType` is set,
    // we always have an `undefined` value to prevent the broadcast listener from disposing during
    // window type changes, causing issues with redelivery of action and double-acks, when
    // the the hook is unmounted before the action callback got called.
    // If this is causing issues in the future we might want to implement a way for each handler
    // to check if it is still registered/active and noop if it isn't.
    const activeWorkspaceWindowType = ignoreActiveWorkspaceWindowType
        ? undefined
        : workspacePanelsStore.activeWorkspaceWindowType;

    useEffect(() => {
        if (!handler || !windowType) {
            return;
        }

        const setupActionHandler = () => {
            const handlerId = id ? `${id}~${v4()}` : v4();
            const ackSource: AckSource<CadNextAction> = {
                id: handlerId,
                windowType,
            };

            const disposer = actionBroadcaster.registerWindowTarget(windowType, {
                id: handlerId,
                handleActions(actions) {
                    for (const action of actions) {
                        const result = handler(action);
                        if (result) {
                            actionBroadcaster.ack(action, ackSource);
                        }
                    }
                },
            });

            actionBroadcasterDisposerRef.current = () => {
                disposer();
            };
        };

        if (ignoreActiveWorkspaceWindowType) {
            setupActionHandler();
            return () => {
                if (actionBroadcasterDisposerRef.current) {
                    actionBroadcasterDisposerRef.current();
                    actionBroadcasterDisposerRef.current = undefined;
                }
            };
        } else {
            const dispose = reaction(
                () => activeWorkspaceWindowType,
                (value) => {
                    if (actionBroadcasterDisposerRef.current) {
                        actionBroadcasterDisposerRef.current();
                        actionBroadcasterDisposerRef.current = undefined;
                    }

                    if (windowType === value) {
                        setupActionHandler();
                    }
                },
                { fireImmediately: true }
            );

            return () => {
                dispose();
                if (actionBroadcasterDisposerRef.current) {
                    actionBroadcasterDisposerRef.current();
                    actionBroadcasterDisposerRef.current = undefined;
                }
            };
        }
    }, [
        actionBroadcaster,
        activeWorkspaceWindowType,
        ignoreActiveWorkspaceWindowType,
        windowType,
        /**
         * Make sure that firing the handler doesn't cause the handler to update.
         * This may be the case if you're seeing your handler fire multiple times for the same action.
         */
        handler,
        id,
    ]);

    return { trigger: actionBroadcaster.trigger };
}
